/**
 * Composable du mode spectateur (SD-3875).
 *
 * Le mode spectateur permet de basculer entre la vue conseiller (chat +
 * artefacts) et une vue dédiée au client (uniquement les simulations en
 * lecture, présentation didactique). L'état est persisté en localStorage
 * par conversation pour rester stable au refresh.
 *
 * Le composable est volontairement isolé du state d'artifacts : il prend des
 * refs en entrée et expose les valeurs dérivées + handlers.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import type { Artifact } from '@ggvie-consulting/artifacts/useArtifacts'
import type { ArtifactTreeNode } from '@ggvie-consulting/sidebar/ArtifactTree.vue'

export const STORAGE_KEY_PREFIX = 'spectator-mode-'

export interface UseSpectatorModeOptions {
  artifacts: Ref<Artifact[]>
  activeArtifactId: Ref<string | null>
  /** Appelé pour resynchroniser la sélection lorsqu'on quitte le mode. */
  setActiveArtifact: (id: string) => void
  /** Ouvre l'artifact panel (utilisé à la sortie du mode spectateur). */
  openArtifactPanel: () => void
}

export interface UseSpectatorModeReturn {
  spectatorMode: Ref<boolean>
  spectatorSimulationId: Ref<string | null>
  hasSimulations: ComputedRef<boolean>
  simulationArtifacts: ComputedRef<Artifact[]>
  /** SD-4203 — arborescence des simulations (kit → dossier sims → simulations). */
  spectatorTree: ComputedRef<ArtifactTreeNode[]>
  spectatorClientName: ComputedRef<string>
  spectatorGreeting: ComputedRef<string>
  activeSpectatorArtifact: ComputedRef<Artifact | null>
  toggleSpectatorMode: (convId?: string) => void
  handleSpectatorSelect: (id: string) => void
  loadSpectatorMode: (convId: string | undefined) => boolean
  persistSpectatorMode: (convId: string | undefined, enabled: boolean) => void
}

export function useSpectatorMode(opts: UseSpectatorModeOptions): UseSpectatorModeReturn {
  const spectatorMode = ref(false)
  const spectatorSimulationId = ref<string | null>(null)

  // SD-4385 — le mode spectateur (vue client, lecture seule) ne montre JAMAIS d'artefacts
  // soft-deletés : sinon les sim_folders supprimés par les régénérations/migrations successives
  // (SD-4365) apparaissent comme des « dossiers de simulation vides ».
  const hasSimulations = computed(() =>
    opts.artifacts.value.some((a) => a.artifactType === 'simulation' && a.isDeleted !== true),
  )
  const simulationArtifacts = computed(() =>
    opts.artifacts.value.filter((a) => a.artifactType === 'simulation' && a.isDeleted !== true),
  )

  // SD-4203 — arborescence des simulations pour le mode spectateur.
  // On reconstruit la hiérarchie kit_folder → sim_folder → simulations afin que
  // la vue spectateur ne présente pas les simulations « tout à plat ». Seules
  // les simulations et leurs dossiers ancêtres sont conservés ; les livrables
  // (phrases de rebond, insights, objections) sont volontairement exclus.
  const spectatorTree = computed<ArtifactTreeNode[]>(() => {
    const all = opts.artifacts.value.filter(
      (a) => a.associated !== false && a.artifactType !== 'kit_rdv' && a.isDeleted !== true,
    )
    const sims = all.filter((a) => a.artifactType === 'simulation')
    if (sims.length === 0) return []

    const byId = new Map(all.map((a) => [a.id, a]))

    // Ensemble à conserver : chaque simulation + la chaîne de ses dossiers parents.
    const keep = new Set<string>()
    for (const sim of sims) {
      keep.add(sim.id)
      let pid = sim.parentArtifactId ?? null
      while (pid && byId.has(pid) && !keep.has(pid)) {
        keep.add(pid)
        pid = byId.get(pid)?.parentArtifactId ?? null
      }
    }

    const visible = all.filter((a) => keep.has(a.id))
    const childrenOf = new Map<string | null, Artifact[]>()
    for (const a of visible) {
      const pid = a.parentArtifactId ?? null
      const key = pid && keep.has(pid) ? pid : null
      const list = childrenOf.get(key)
      if (list) list.push(a)
      else childrenOf.set(key, [a])
    }

    const toNode = (a: Artifact): ArtifactTreeNode => {
      const kids = (childrenOf.get(a.id) ?? []).map(toNode)
      return {
        id: a.id,
        label: a.title,
        // Dossier (kit_folder / sim_folder) → groupe expand/collapse non cliquable.
        artifactType: a.isFolder ? undefined : a.artifactType,
        children: kids.length > 0 ? kids : undefined,
        isLocked: a.isLocked === true,
        isStale: a.isStale === true,
        isDeleted: a.isDeleted === true,
        // Lecture seule : jamais de corbeille en mode spectateur.
        isDeletable: false,
      }
    }

    const roots = childrenOf.get(null) ?? []
    // Kit le plus récent en premier ; simulations hors dossier (version null) ensuite.
    return roots.sort((x, y) => (y.version ?? 0) - (x.version ?? 0)).map(toNode)
  })

  const spectatorClientName = computed(() => {
    const profile = opts.artifacts.value.find((a) => a.artifactType === 'profile')
    if (!profile) return ''
    const fields = (profile.content?.fields ?? {}) as Record<string, string>
    return fields.nom || ''
  })

  const spectatorGreeting = computed(() => {
    const nom = spectatorClientName.value
    return nom
      ? `Bonjour ${nom}, voici vos simulations retraite`
      : 'Bonjour, voici vos simulations retraite'
  })

  const activeSpectatorArtifact = computed(() => {
    if (!spectatorSimulationId.value) return null
    return simulationArtifacts.value.find((a) => a.id === spectatorSimulationId.value) ?? null
  })

  function storageKey(convId: string): string {
    return STORAGE_KEY_PREFIX + convId
  }

  function persistSpectatorMode(convId: string | undefined, enabled: boolean): void {
    if (!convId) return
    try {
      localStorage.setItem(storageKey(convId), JSON.stringify(enabled))
    } catch {
      /* silent */
    }
  }

  function loadSpectatorMode(convId: string | undefined): boolean {
    if (!convId) return false
    try {
      return localStorage.getItem(storageKey(convId)) === 'true'
    } catch {
      return false
    }
  }

  function toggleSpectatorMode(convId?: string): void {
    spectatorMode.value = !spectatorMode.value
    if (spectatorMode.value) {
      // Conseiller → Spectateur : on synchronise la sélection.
      const currentIsSimulation =
        opts.activeArtifactId.value &&
        simulationArtifacts.value.some((a) => a.id === opts.activeArtifactId.value)
      if (currentIsSimulation) {
        spectatorSimulationId.value = opts.activeArtifactId.value
      } else if (simulationArtifacts.value.length > 0) {
        // SD-4365 — défaut = carte fusionnée capi + éco-impôt (repli sur la 1re sim si absente).
        const preferred =
          simulationArtifacts.value.find(
            (a) => a.content.componentType === 'SimulationCapiEcoResults',
          ) ?? simulationArtifacts.value[0]
        spectatorSimulationId.value = preferred.id
      }
    } else if (spectatorSimulationId.value) {
      // Spectateur → Conseiller : restaurer la sélection dans le panel.
      opts.setActiveArtifact(spectatorSimulationId.value)
      opts.openArtifactPanel()
    }
    persistSpectatorMode(convId, spectatorMode.value)
  }

  function handleSpectatorSelect(id: string): void {
    spectatorSimulationId.value = id
  }

  return {
    spectatorMode,
    spectatorSimulationId,
    hasSimulations,
    simulationArtifacts,
    spectatorTree,
    spectatorClientName,
    spectatorGreeting,
    activeSpectatorArtifact,
    toggleSpectatorMode,
    handleSpectatorSelect,
    loadSpectatorMode,
    persistSpectatorMode,
  }
}
